import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Container, 
  Typography, 
  Box, 
  Grid as MuiGrid, 
  Card, 
  CardContent, 
  Divider, 
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { categoriesApi } from '../api/api';
import Loader from '../components/Loader';
import ErrorDisplay from '../components/ErrorDisplay';
import { Category } from '../types/api';
import CategoryIconComponent from '../components/CategoryIcon';

// Create a Grid component that doesn't require component prop
const Grid = (props: any) => <MuiGrid {...props} />;

const CategoriesPage: React.FC = () => {
  // Fetch all categories
  const { data: categoriesData, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.getCategories,
  });

  // Group categories by parent
  const groupedCategories = React.useMemo(() => {
    if (!categoriesData) return {};
    
    // First, find all parent categories
    const parentCategories = categoriesData.items.filter(cat => !cat.parentId && ![177597826, 142312356].includes(cat.id));
    
    // Create a map of parent categories with their children
    const categoryMap: Record<number, { parent: Category, children: Category[] }> = {};
    
    parentCategories.forEach(parent => {
      categoryMap[parent.id] = {
        parent,
        children: categoriesData.items.filter(cat => cat.parentId === parent.id)
      };
    });
    
    return categoryMap;
  }, [categoriesData]);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Loader message="Loading categories..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorDisplay 
          message="Failed to load categories"
          error={error as Error}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          All Categories
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Box>
      
      <Grid container spacing={4}>
        {Object.values(groupedCategories).map(({ parent, children }) => (
          <Grid item xs={12} md={6} lg={4} key={parent.id}>
            <Card 
              elevation={1}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CategoryIconComponent 
                    category={parent}
                    size={56}
                  />
                  <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h5" 
                      component={RouterLink} 
                      to={`/category/${parent.id}`} 
                      sx={{ 
                        color: 'primary.main', 
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&:hover': {
                          color: 'primary.dark'
                        }
                      }}
                    >
                      {parent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {parent.productCount || 0} products
                    </Typography>
                  </Box>
                </Box>
                
                {children.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Subcategories
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {children.map((child: Category) => (
                        <Chip
                          key={child.id}
                          label={`${child.name} ${child.productCount ? `(${child.productCount})` : ''}`}
                          component={RouterLink}
                          to={`/category/${child.id}`}
                          clickable
                          size="small"
                          sx={{
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'primary.light',
                              color: 'white',
                              borderColor: 'primary.light',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* If no categories are found */}
        {Object.keys(groupedCategories).length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6">No categories found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check back later or browse our products directly.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default CategoriesPage; 